<script setup>
import { useI18n } from 'vue-i18n'
import { Button, Badge } from '@drama/ui'
import { Play, RefreshCw } from '@lucide/vue'
import { videoSupplierCostText } from '../../lib/video-supplier-cost.js'

const props = defineProps({
  execution: { type: Object, required: true },
  selectedShotIndex: { type: Number, default: null },
  selectedShot: { type: Object, default: null },
  executionVideoUrl: { type: String, default: null },
  currentVideoTime: { type: Number, default: 0 },
  totalShotsDurationMs: { type: Number, default: 0 },
  playheadPercent: { type: Number, default: 0 },
  mediaState: { type: String, default: 'not-recorded' },
  castRoleList: { type: Array, default: () => [] },
  promptComparison: { type: String, default: 'unknown' },
})

const emit = defineEmits([
  'selectShot',
  'togglePlay',
  'seekPlannedShot',
  'timeUpdate',
  'videoMetadata',
  'videoReady',
  'videoError',
])

const { t } = useI18n()

function formatSeconds(ms) {
  if (!Number.isFinite(ms)) return '0:00.00'
  const totalSec = ms / 1000
  const mins = Math.floor(totalSec / 60)
  const secs = (totalSec % 60).toFixed(2).padStart(5, '0')
  return `${mins}:${secs}`
}

function getShotDuration(shot) {
  if (shot && Number.isFinite(shot.start_ms) && Number.isFinite(shot.end_ms) && shot.end_ms >= shot.start_ms) {
    return ((shot.end_ms - shot.start_ms) / 1000).toFixed(2) + 's'
  }
  return ''
}

function getShotFlex(shot) {
  if (shot && Number.isFinite(shot.start_ms) && Number.isFinite(shot.end_ms) && shot.end_ms > shot.start_ms) {
    return Math.max(1000, Math.round(shot.end_ms - shot.start_ms))
  }
  return 4000
}

function statusTone(status) {
  if (status === 'failed' || status === 'rejected' || status === 'error') return 'danger'
  if (status === 'media_ready' || status === 'ok') return 'success'
  if (status === 'producing' || status === 'running') return 'info'
  return 'neutral'
}

function executionStatus(status) {
  const known = ['queued', 'producing', 'failed', 'review_ready', 'rejected', 'media_ready']
  return t(`storyFilm.executionLog.${known.includes(status) ? `status_${status}` : 'status_unknown'}`)
}

function callStatus(status) {
  if (status === 'submitting') return t('storyFilm.executionLog.call_submitting')
  if (status === 'submitted') return t('storyFilmReview.callSubmitted')
  const known = ['ok', 'error', 'failed', 'queued', 'running']
  if (known.includes(status)) return t(`storyFilm.executionLog.call_${status}`)
  return t(status == null ? 'storyFilm.executionLog.call_unknown' : 'storyFilm.executionLog.status_unknown')
}
</script>

<template>
  <div class="story-film-detail-stage">
    <!-- 1. 竖屏放映屏幕 -->
    <div class="cinema-screen">
      <video
        v-if="executionVideoUrl"
        :src="executionVideoUrl"
        controls
        playsinline
        preload="metadata"
        @timeupdate="emit('timeUpdate', $event)"
        @loadedmetadata="emit('videoMetadata', $event)"
        @loadeddata="emit('videoReady', $event)"
        @canplay="emit('videoReady', $event)"
        @error="emit('videoError', $event)"
      />
      <div v-else-if="mediaState === 'loading'" class="cinema-screen__placeholder" role="status">
        <RefreshCw class="spin-icon" :size="32" />
        <p>{{ t('storyFilm.executionLog.mediaLoading') }}</p>
      </div>
      <div v-else class="cinema-screen__placeholder">
        <b>实际画面按提交鉴权加载</b>
        <span>{{ (execution.media?.duration_ms ? execution.media.duration_ms / 1000 : 0).toFixed(2) }}s · 9:16</span>
      </div>
      <span class="cinema-screen__tag">媒体播放 · 只读</span>
      <div class="cinema-screen__meta">
        <span>{{ formatSeconds(currentVideoTime * 1000) }} / {{ formatSeconds(totalShotsDurationMs) }}</span>
        <span>镜 {{ selectedShot?.index ?? '—' }} · 计划位置</span>
      </div>
    </div>

    <!-- 2. 播放控制条 -->
    <div class="transport-bar">
      <Button size="sm" variant="secondary" @click="emit('togglePlay')">
        <Play :size="13" />
        <span>播放/暂停</span>
      </Button>
      <span class="transport-bar__time">{{ formatSeconds(currentVideoTime * 1000) }} / {{ formatSeconds(totalShotsDurationMs) }}</span>
      <div class="spacer" />
      <Button v-if="selectedShot && Number.isFinite(selectedShot.start_ms)" size="sm" variant="ghost" @click="emit('seekPlannedShot')">
        从镜 {{ selectedShot.index }} 计划起点播放
      </Button>
    </div>

    <!-- 3. 分段时间轴 (Timeline) -->
    <div class="timeline-bar">
      <button
        v-for="shot in execution.shots"
        :key="shot.index"
        type="button"
        class="timeline-seg"
        :class="{ 'is-active': selectedShotIndex === shot.index }"
        :style="{ flex: getShotFlex(shot) }"
        :title="`镜 ${shot.index}: ${formatSeconds(shot.start_ms)}–${formatSeconds(shot.end_ms)}`"
        @click="emit('selectShot', shot)"
      >
        {{ shot.index }}
      </button>
      <div class="timeline-playhead" :style="{ left: `${playheadPercent}%` }" />
    </div>
    <div class="hint-text timeline-hint">
      时间轴按计划切点绘制（红线为当前播放位置）；实际切点数据库未记录，仅供核对参考。
    </div>

    <!-- 4. 镜头概要胶囊 (Shot Facts) -->
    <div v-if="selectedShot" class="shot-facts-row">
      <span class="badge-pill">镜 {{ selectedShot.index }} / {{ execution.shots.length }}</span>
      <span v-if="castRoleList.length" class="badge-pill">{{ castRoleList.join(', ') }}</span>
      <span class="badge-pill">{{ getShotDuration(selectedShot) }}</span>
      <span class="badge-pill" :class="promptComparison === 'same' ? 'is-ok' : ''">提示词 {{ promptComparison === 'same' ? '逐字相同' : '已留存' }}</span>
      <span class="badge-pill" :class="statusTone(selectedShot.call?.status) === 'success' ? 'is-ok' : ''">调用 {{ callStatus(selectedShot.call?.status) }}</span>
      <span v-if="selectedShot.call?.duration_ms" class="badge-pill">{{ (selectedShot.call.duration_ms / 1000).toFixed(1) }}s</span>
    </div>

    <!-- 5. 镜头导航网格 (Shot Cards Grid) -->
    <div class="shot-grid-section">
      <header class="shot-grid-header">
        <span class="shot-grid-title">镜头导航 · 共 {{ execution.shots.length }} 镜</span>
        <span class="hint-text">点击分镜跳转播放并联动右侧审查台</span>
      </header>
      <div class="shot-cards-grid">
        <button
          v-for="shot in execution.shots"
          :key="shot.index"
          type="button"
          class="shot-card-btn"
          :class="{ 'is-active': selectedShotIndex === shot.index }"
          @click="emit('selectShot', shot)"
        >
          <div class="shot-card-top">
            <b>镜 {{ shot.index }}</b>
            <span class="shot-time-tag">{{ formatSeconds(shot.start_ms) }}</span>
            <span class="card-status-dot" :class="`is-${statusTone(shot.call?.status)}`" />
          </div>
          <div class="shot-card-bottom">
            <span class="shot-status-badge" :class="`is-${statusTone(shot.call?.status)}`">
              {{ callStatus(shot.call?.status) }}
            </span>
            <span v-if="shot.cast && shot.cast.length" class="shot-cast-text">
              {{ Array.isArray(shot.cast) ? shot.cast.join(', ') : shot.cast }}
            </span>
          </div>
        </button>
      </div>
    </div>

    <!-- 6. 制作核对结论卡片 (Summary Card) -->
    <div class="summary-card">
      <header class="summary-card__head">
        <h5>本次制作结论（只读）</h5>
        <Badge :variant="statusTone(execution.status)" size="sm">{{ executionStatus(execution.status) }}</Badge>
      </header>
      <div class="summary-grid">
        <div class="summary-cell">
          调用记录<b>{{ execution.shots.filter(s => s.call?.status === 'ok').length }}/{{ execution.shots.length }} ok</b>
          <span class="cell-sub">单条成功不代表整片通过</span>
        </div>
        <div class="summary-cell">
          时长<b>计划 {{ formatSeconds(totalShotsDurationMs) }}</b>
          <span class="cell-sub">实际切点未记录</span>
        </div>
        <div class="summary-cell">
          提示词<b>{{ promptComparison === 'same' ? '计划 = 实发' : '实发已留存' }}</b>
          <span class="cell-sub">当前镜 {{ selectedShot?.actual_prompt ? selectedShot.actual_prompt.length + ' 字符' : '未提供' }}</span>
        </div>
        <div class="summary-cell">
          成本<b>{{ selectedShot?.predicted_cost?.points != null ? selectedShot.predicted_cost.points + ' 积分/镜' : '未记录' }}</b>
          <span class="cell-sub">记账实耗 {{ videoSupplierCostText(selectedShot?.call, t) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.story-film-detail-stage {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-md);
  min-width: 0;
  width: 100%;
}

.cinema-screen {
  position: relative;
  align-self: center;
  width: 300px;
  aspect-ratio: 9 / 16;
  border-radius: var(--drama-radius-md);
  overflow: hidden;
  background: #0b0b10;
  border: 1px solid var(--drama-color-border-strong);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cinema-screen video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.cinema-screen__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: radial-gradient(circle at 50% 35%, #3a3f4a, #15181d 70%);
  color: #cfd4dc;
  text-align: center;
}

.cinema-screen__placeholder b {
  font-size: var(--drama-font-size-sm);
  color: #e8eaee;
}

.cinema-screen__placeholder span {
  font-size: var(--drama-font-size-xs);
  color: #9aa3af;
}

.cinema-screen__tag {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  border-radius: var(--drama-radius-full);
  padding: 3px 10px;
  font-size: var(--drama-font-size-xs);
}

.cinema-screen__meta {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px 10px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.75), transparent);
  color: #fff;
  font-size: var(--drama-font-size-xs);
  display: flex;
  justify-content: space-between;
}

.transport-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.transport-bar__time {
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.spacer {
  flex: 1;
}

.timeline-bar {
  position: relative;
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 34px;
}

.timeline-seg {
  position: relative;
  height: 28px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-xs, 4px);
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.timeline-seg:hover {
  background: var(--drama-color-surface);
}

.timeline-seg.is-active {
  background: color-mix(in srgb, var(--drama-color-primary) 18%, var(--drama-color-surface));
  border-color: var(--drama-color-primary);
  color: var(--drama-color-text);
  font-weight: 700;
}

.timeline-playhead {
  position: absolute;
  top: -4px;
  bottom: -2px;
  width: 2px;
  background: var(--drama-color-danger);
  pointer-events: none;
  transition: left 0.1s linear;
}

.timeline-hint {
  margin-top: -4px;
  font-size: var(--drama-font-size-xs);
}

.shot-facts-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.badge-pill.is-ok {
  background: color-mix(in srgb, var(--drama-color-success) 14%, transparent);
  color: var(--drama-color-success);
}

.shot-grid-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
}

.shot-grid-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.shot-grid-title {
  font-size: var(--drama-font-size-sm);
  font-weight: 600;
  color: var(--drama-color-text);
}

.shot-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(145px, 1fr));
  gap: 10px;
}

.shot-card-btn {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 10px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.shot-card-btn:hover {
  border-color: var(--drama-color-border-strong);
  box-shadow: var(--drama-shadow-xs);
}

.shot-card-btn.is-active {
  border-color: var(--drama-color-primary);
  background: color-mix(in srgb, var(--drama-color-primary) 8%, var(--drama-color-surface));
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--drama-color-primary) 40%, transparent);
}

.shot-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.shot-card-top b {
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text);
}

.shot-time-tag {
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.card-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.card-status-dot.is-success { background: var(--drama-color-success); }
.card-status-dot.is-danger { background: var(--drama-color-danger); }
.card-status-dot.is-info { background: var(--drama-color-info); }
.card-status-dot.is-neutral { background: var(--drama-color-border-strong); }

.shot-card-bottom {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.shot-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--drama-radius-xs, 4px);
  font-size: var(--drama-font-size-xs);
  background: var(--drama-color-surface-alt);
}

.shot-status-badge.is-success {
  background: color-mix(in srgb, var(--drama-color-success) 12%, transparent);
  color: var(--drama-color-success);
}

.shot-status-badge.is-danger {
  background: color-mix(in srgb, var(--drama-color-danger) 12%, transparent);
  color: var(--drama-color-danger);
}

.shot-status-badge.is-info {
  background: color-mix(in srgb, var(--drama-color-info) 12%, transparent);
  color: var(--drama-color-info);
}

.shot-cast-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-card {
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.summary-card__head h5 {
  margin: 0;
  font-size: var(--drama-font-size-sm);
  font-weight: 600;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.summary-cell {
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  padding: 10px 12px;
  background: var(--drama-color-surface);
  font-size: var(--drama-font-size-xs);
  line-height: 1.5;
}

.summary-cell b {
  display: block;
  font-size: var(--drama-font-size-sm);
  margin-top: 2px;
}

.cell-sub {
  display: block;
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.hint-text {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.spin-icon {
  animation: spin 1s linear infinite;
  color: var(--drama-color-primary);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>

